import { useTranslation } from "react-i18next";
import UiLink from "../../../../components/link/Link.component";
import UiPhoneCaller from "../../../../components/phone-caller/PhoneCaller.component";
import { TodaysAppointmentCard, formatAppointmentTime } from "./appointments-rail.service";

export interface AppointmentCardProps {
	card: TodaysAppointmentCard;
}

/**
 * One appointment card in the Today's Appointments rail.
 *
 * There is NO status pill: the system stores only `cancelled`, so a "Confirmed"
 * pill would be on every card forever — Maria dropped it (answer #1).
 */
const AppointmentCard = ({ card }: AppointmentCardProps) => {
	const { t } = useTranslation("common");

	const typeLabel = t(`ENUMS.APPOINTMENTS_TYPE.${card.type}`, { defaultValue: card.type });
	const durationLabel =
		card.durationInMinutes != null
			? t("HOME.RAIL.APPOINTMENTS.DURATION", { count: card.durationInMinutes })
			: null;
	const fullTypeLabel = durationLabel ? `${typeLabel} (${durationLabel})` : typeLabel;

	return (
		<li
			className="appointment-card"
			data-testid={`appointment-card-${card.id}`}
		>
			<div
				className="appointment-card__header"
				data-testid={`appointment-card-header-${card.id}`}
			>
				<span className="appointment-card__time">
					{formatAppointmentTime(card.startsAt)}
				</span>

				{/* Call control only when participant has a phone number. This is the platform's
					existing control as-is — it shows the number, asks first and handles being already
					on a call (Maria's answer #2); the ticket's "immediately triggers an outbound call"
					is deliberately NOT implemented as a one-click uncancellable dial. No number → no
					control, never a dead button. */}
				{card.participant.phone && (
					<UiPhoneCaller
						phoneNumber={card.participant.phone}
						name={card.participant.name}
						size="p-button-xs"
					/>
				)}
			</div>

			<div
				className="appointment-card__participant"
				data-testid={`appointment-card-participant-${card.id}`}
			>
				{/* Participant line. Per Maria (KAN-2554) this follows the rest of the
					platform: ONLY the Kannact ID is the link, the name is plain text.
					Falls back to plain text when there is no route id to link to, so the
					line is never a link to nowhere. */}
				{card.participant.kannactId &&
					(card.participant.routeId ? (
						<UiLink
							id={`appointment-participant-${card.id}`}
							to={`/participant/${card.participant.routeId}/dashboard`}
							label={card.participant.kannactId}
						/>
					) : (
						<span className="kannact-id">{card.participant.kannactId}</span>
					))}
				{card.participant.name && (
					<span
						className="full-name"
						title={card.participant.name}
					>
						{card.participant.name}
					</span>
				)}
			</div>

			<div
				className="appointment-card__type"
				data-testid={`appointment-card-type-${card.id}`}
			>
				{fullTypeLabel}
			</div>
		</li>
	);
};

export default AppointmentCard;
